import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { ReactNode } from 'react';

import type { BoardSummary, Principal, UpdateTicketInput, WorkflowDefinition } from '../shared/index.js';
import {
  COST_CENTER_LABELS,
  EMPLOYMENT_TYPE_LABELS,
  TICKET_PRIORITIES,
  TICKET_PRIORITY_LABELS,
  availableTransitions,
  findStage,
  hasWorkingAccess,
  isAdmin,
  isPrivileged,
  isUnrestrictedWorkflow,
} from '../shared/index.js';

import { ApiError } from '../api/client.js';
import type { DirectoryUser } from '../api/endpoints.js';
import { commentApi, ticketApi } from '../api/endpoints.js';
import { queryKeys } from '../app/query-client.js';
import { useDepartments, useProjects, useAssignableUsers } from '../hooks/use-directory.js';
import { AttachmentList } from './attachment-list.js';
import { TicketHistory } from './ticket-history.js';
import { Select } from './select.js';
import { Alert, Badge, Button, Spinner } from './ui.js';
import { personLabel } from './ticket-card.js';

interface TicketPanelProps {
  readonly ticketId: string;
  readonly boardId: string;
  readonly board: BoardSummary;
  readonly workflow: WorkflowDefinition;
  readonly principal: Principal;
  readonly people: ReadonlyMap<string, DirectoryUser>;
  readonly onClose: () => void;
}

const INLINE_TRIGGER =
  'flex h-8 w-full max-w-60 items-center gap-2 rounded-field border border-transparent px-2 text-left text-[13.5px] font-semibold text-navy transition hover:border-hairline hover:bg-mist/60 focus:border-royal/55 focus:outline-none dark:text-dark-ink dark:hover:border-dark-line dark:hover:bg-white/5';

function Row({
  label,
  children,
}: {
  readonly label: string;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <div className="flex items-start gap-3 py-2">
      <span className="w-28 shrink-0 pt-0.5 text-[12px] font-semibold tracking-[0.04em] text-muted uppercase">
        {label}
      </span>
      <span className="min-w-0 flex-1 text-[13.5px] text-navy dark:text-dark-ink">{children}</span>
    </div>
  );
}

export function TicketPanel({
  ticketId,
  boardId,
  board,
  workflow,
  principal,
  people,
  onClose,
}: TicketPanelProps): ReactNode {
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const [comment, setComment] = useState('');
  const [internal, setInternal] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [activity, setActivity] = useState<'comments' | 'history'>('comments');

  const { data: ticket, isLoading } = useQuery({
    queryKey: queryKeys.tickets.detail(ticketId),
    queryFn: () => ticketApi.get(ticketId),
  });

  const { data: comments } = useQuery({
    queryKey: queryKeys.tickets.comments(ticketId),
    queryFn: () => commentApi.list(ticketId),
  });

  const { data: events } = useQuery({
    queryKey: queryKeys.tickets.events(ticketId),
    queryFn: () => ticketApi.events(ticketId),
    enabled: activity === 'history',
  });

  const refresh = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: queryKeys.tickets.detail(ticketId),
      }),
      queryClient.invalidateQueries({
        queryKey: queryKeys.boards.cards(boardId),
      }),
      queryClient.invalidateQueries({
        queryKey: queryKeys.tickets.events(ticketId),
      }),
    ]);
  };

  const transition = useMutation({
    mutationFn: (transitionId: string) =>
      ticketApi.runTransition(ticketId, transitionId, note.trim() === '' ? undefined : note.trim()),
    onSuccess: async () => {
      setNote('');
      setProblem(null);
      await refresh();
    },
    onError: (error: unknown) => {
      setProblem(error instanceof ApiError ? error.message : 'That move did not go through.');
    },
  });

  const addComment = useMutation({
    mutationFn: () => commentApi.add(ticketId, comment.trim(), internal),
    onSuccess: async () => {
      setComment('');
      await queryClient.invalidateQueries({
        queryKey: queryKeys.tickets.comments(ticketId),
      });
    },
    onError: (error: unknown) => {
      setProblem(error instanceof ApiError ? error.message : 'The comment was not saved.');
    },
  });

  const update = useMutation({
    mutationFn: (input: UpdateTicketInput) => ticketApi.update(ticketId, input),
    onSuccess: async () => {
      setProblem(null);
      await refresh();
    },
    onError: (error: unknown) => {
      setProblem(error instanceof ApiError ? error.message : 'That change was not saved.');
    },
  });

  const removeTicket = useMutation({
    mutationFn: () => ticketApi.remove(ticketId),
    onSuccess: async () => {
      setConfirmingDelete(false);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.boards.cards(boardId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.tickets.all }),
      ]);
      onClose();
    },
    onError: (error: unknown) => {
      setConfirmingDelete(false);
      setProblem(error instanceof ApiError ? error.message : 'The request could not be deleted.');
    },
  });

  const mayDelete = ticket !== undefined && isAdmin(principal);

  const mayArchive =
    ticket !== undefined &&
    (isPrivileged(principal) ||
      ticket.requesterId === principal.id ||
      hasWorkingAccess(principal, ticket.departmentSlug));

  const archive = useMutation({
    mutationFn: (next: 'archive' | 'restore') =>
      next === 'archive' ? ticketApi.archive(ticketId) : ticketApi.unarchive(ticketId),
    onSuccess: async () => {
      setProblem(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.boards.cards(boardId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.boards.archived(boardId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.tickets.detail(ticketId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.tickets.events(ticketId) }),
      ]);
    },
    onError: (error: unknown) => {
      setProblem(error instanceof ApiError ? error.message : 'That did not work.');
    },
  });

  const mayEdit =
    ticket !== undefined &&
    ticket.status !== 'closed' &&
    (isPrivileged(principal) ||
      ticket.requesterId === principal.id ||
      hasWorkingAccess(principal, ticket.departmentSlug));

  const assignable = useAssignableUsers(mayEdit ? (ticket?.departmentId ?? null) : null);

  const assigneeOptions = [
    { value: '', label: 'Unassigned' },
    ...assignable.map((person) => ({
      value: person.id,
      label: `${person.firstName} ${person.lastName}`,
    })),
    ...(ticket?.assigneeId !== null &&
    ticket?.assigneeId !== undefined &&
    !assignable.some((p) => p.id === ticket.assigneeId)
      ? [{ value: ticket.assigneeId, label: personLabel(people, ticket.assigneeId) }]
      : []),
  ];

  const { departments } = useDepartments();
  const projects = useProjects();

  const mayMoveDepartment = mayEdit && board.intakeTarget === null;

  const departmentOptions = departments.map((department) => ({
    value: department.id,
    label: department.name,
  }));

  const projectRequired =
    ticket?.fieldSet === 'recruitment' ||
    (ticket?.fieldSet === 'purchase' && ticket.costCenter === 'client');

  const pickableProjects =
    board.requiresProjectAuthorisation && !isPrivileged(principal)
      ? projects.filter((project) =>
          principal.managedProjectIds.some((managed) => String(managed) === project.id),
        )
      : projects;

  const projectOptions = [
    ...(projectRequired ? [] : [{ value: '', label: 'None' }]),
    ...pickableProjects.map((project) => ({ value: project.id, label: project.name })),
    ...(ticket?.projectId !== null &&
    ticket?.projectId !== undefined &&
    !pickableProjects.some((p) => p.id === ticket.projectId)
      ? [
          {
            value: ticket.projectId,
            label: projects.find((p) => p.id === ticket.projectId)?.name ?? 'Current project',
          },
        ]
      : []),
  ];

  const moves =
    ticket === undefined
      ? []
      : availableTransitions(workflow, principal, {
          stageId: ticket.stageId,
          requesterId: ticket.requesterId,
          assigneeId: ticket.assigneeId,
          departmentSlug: ticket.departmentSlug,
        });

  const stage = ticket === undefined ? undefined : findStage(workflow, ticket.stageId);

  return (
    <>
      <button
        type="button"
        aria-label="Close the request"
        onClick={onClose}
        className="fixed inset-0 z-30 bg-navy-deep/40 backdrop-blur-sm lg:hidden"
      />

      <aside className="fixed inset-y-0 right-0 z-40 flex w-full flex-col border-l border-hairline bg-white shadow-menu sm:max-w-[520px] dark:border-dark-line dark:bg-dark-panel">
        <header className="flex h-topbar shrink-0 items-center gap-3 border-b border-hairline px-5 dark:border-dark-line">
          <span className="text-[12px] font-bold tracking-[0.06em] text-muted">
            {ticket === undefined ? '' : `SBG#${String(ticket.number)}`}
          </span>
          {stage === undefined ? null : <Badge tone={stage.tone}>{stage.label}</Badge>}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close the request"
            className="ml-auto rounded-field px-2 py-1 text-[18px] leading-none text-muted hover:bg-navy/5 hover:text-navy dark:hover:bg-white/5"
          >
            ×
          </button>
        </header>

        {isLoading || ticket === undefined ? (
          <div className="grid flex-1 place-items-center text-muted">
            <Spinner className="size-5" />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-5 py-4">
            {mayEdit ? (
              <input
                key={ticket.title}
                defaultValue={ticket.title}
                aria-label="Title"
                onBlur={(event) => {
                  const next = event.target.value.trim();
                  if (next === '' || next === ticket.title) {
                    event.target.value = ticket.title;
                    return;
                  }
                  update.mutate({ title: next });
                }}
                className="-mx-2 w-[calc(100%+1rem)] rounded-field border border-transparent px-2 font-display text-[22px] leading-tight font-extrabold tracking-[-0.02em] text-navy transition hover:border-hairline hover:bg-mist/60 focus:border-royal/55 focus:bg-white focus:outline-none dark:text-dark-ink dark:hover:border-dark-line dark:hover:bg-white/5"
              />
            ) : (
              <h2 className="font-display text-[22px] leading-tight font-extrabold tracking-[-0.02em] text-navy dark:text-dark-ink">
                {ticket.title}
              </h2>
            )}

            {problem === null ? null : (
              <div className="mt-3">
                <Alert tone="error">{problem}</Alert>
              </div>
            )}

            {mayEdit ? (
              <textarea
                key={ticket.description ?? ''}
                defaultValue={ticket.description ?? ''}
                aria-label="Description"
                rows={4}
                placeholder="Add a description"
                onBlur={(event) => {
                  const next = event.target.value.trim();
                  if (next === (ticket.description ?? '')) return;
                  update.mutate({ description: next === '' ? null : next });
                }}
                className="mt-3 w-full rounded-field border border-transparent px-2 py-1.5 text-[14px] leading-relaxed text-navy/80 transition hover:border-hairline hover:bg-mist/60 focus:border-royal/55 focus:bg-white focus:outline-none dark:text-dark-ink/80 dark:hover:border-dark-line dark:hover:bg-white/5"
              />
            ) : ticket.description === null || ticket.description.length === 0 ? null : (
              <p className="mt-3 text-[14px] leading-relaxed whitespace-pre-wrap text-navy/80 dark:text-dark-ink/80">
                {ticket.description}
              </p>
            )}

            <div className="mt-5 divide-y divide-hairline border-y border-hairline dark:divide-dark-line dark:border-dark-line">
              <Row label="Board">{ticket.boardName}</Row>
              <Row label="Requester">{personLabel(people, ticket.requesterId)}</Row>
              <Row label="Assignee">
                {mayEdit ? (
                  <Select
                    label="Assignee"
                    className={INLINE_TRIGGER}
                    value={ticket.assigneeId ?? ''}
                    options={assigneeOptions}
                    onChange={(next) => {
                      update.mutate({ assigneeId: next === '' ? null : next });
                    }}
                  />
                ) : (
                  personLabel(people, ticket.assigneeId)
                )}
              </Row>
              <Row label="Department">
                {mayMoveDepartment ? (
                  <Select
                    label="Department"
                    className={INLINE_TRIGGER}
                    value={ticket.departmentId}
                    options={departmentOptions}
                    onChange={(next) => {
                      update.mutate(
                        ticket.assigneeId === null
                          ? { departmentId: next }
                          : { departmentId: next, assigneeId: null },
                      );
                    }}
                  />
                ) : (
                  ticket.departmentSlug
                )}
              </Row>
              <Row label="Priority">
                {mayEdit ? (
                  <Select
                    label="Priority"
                    className={INLINE_TRIGGER}
                    value={ticket.priority}
                    options={TICKET_PRIORITIES.map((priority) => ({
                      value: priority,
                      label: TICKET_PRIORITY_LABELS[priority],
                    }))}
                    onChange={(next) => {
                      update.mutate({ priority: next as UpdateTicketInput['priority'] });
                    }}
                  />
                ) : (
                  TICKET_PRIORITY_LABELS[ticket.priority]
                )}
              </Row>
              {mayEdit || ticket.projectId !== null ? (
                <Row label="Project">
                  {mayEdit ? (
                    <Select
                      label="Project"
                      className={INLINE_TRIGGER}
                      value={ticket.projectId ?? ''}
                      options={projectOptions}
                      onChange={(next) => {
                        update.mutate({ projectId: next === '' ? null : next });
                      }}
                    />
                  ) : (
                    (projects.find((project) => project.id === ticket.projectId)?.name ?? '—')
                  )}
                </Row>
              ) : null}

              <Row label="Raised">{new Date(ticket.createdAt).toLocaleString()}</Row>

              {ticket.costCenter === null ? null : (
                <Row label="Cost center">{COST_CENTER_LABELS[ticket.costCenter]}</Row>
              )}
              {ticket.estimatedAmount === null ? null : (
                <Row label="Estimated">
                  {ticket.estimatedAmount.toLocaleString()} {ticket.currency ?? ''}
                </Row>
              )}
              {ticket.neededBy === null ? null : <Row label="Needed by">{ticket.neededBy}</Row>}
              {ticket.deliveryLocation === null ? null : (
                <Row label="Deliver to">{ticket.deliveryLocation}</Row>
              )}
              {ticket.projectNote === null ? null : (
                <Row label="Project note">{ticket.projectNote}</Row>
              )}

              {ticket.position === null ? null : <Row label="Position">{ticket.position}</Row>}
              {ticket.employmentType === null ? null : (
                <Row label="Employment">{EMPLOYMENT_TYPE_LABELS[ticket.employmentType]}</Row>
              )}
              {ticket.numberOfPeople === null ? null : (
                <Row label="People">{ticket.numberOfPeople}</Row>
              )}
              {ticket.language === null ? null : <Row label="Language">{ticket.language}</Row>}
              {ticket.deadline === null ? null : (
                <Row label="Deadline">{new Date(ticket.deadline).toLocaleString()}</Row>
              )}
            </div>

            <section className="mt-6">
              <h3 className="eyebrow mb-2 text-muted">What you can do</h3>

              {moves.length === 0 ? (
                <p className="text-[13.5px] text-muted">
                  {isUnrestrictedWorkflow(workflow)
                    ? 'This board has no set moves, so drag the card to any column you need.'
                    : 'Nothing is open to you on this request right now.'}
                </p>
              ) : (
                <>
                  <textarea
                    value={note}
                    onChange={(event) => {
                      setNote(event.target.value);
                    }}
                    rows={2}
                    placeholder="Optional note that goes into the history"
                    className="mb-2.5 w-full rounded-field border-[1.5px] border-navy/20 bg-field px-3 py-2 text-[13.5px] text-navy outline-none focus:border-royal/55 focus:bg-white dark:border-dark-line dark:bg-dark-field dark:text-dark-ink"
                  />
                  <div className="flex flex-wrap gap-2">
                    {moves.map((move) => (
                      <Button
                        key={move.id}
                        size="sm"
                        variant={move.forbidSelfService ? 'primary' : 'secondary'}
                        loading={transition.isPending && transition.variables === move.id}
                        onClick={() => {
                          transition.mutate(move.id);
                        }}
                      >
                        {move.label}
                      </Button>
                    ))}
                  </div>
                </>
              )}
            </section>

            <AttachmentList ticketId={ticketId} canAttach={ticket.status !== 'closed'} />

            <section className="mt-7">
              <div className="mb-3 flex items-center gap-2">
                <h3 className="eyebrow text-muted">Activity</h3>

                <span className="ml-auto flex rounded-field border border-hairline bg-white p-0.5 dark:border-dark-line dark:bg-dark-field">
                  {(['comments', 'history'] as const).map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => {
                        setActivity(tab);
                      }}
                      className={`rounded-[9px] px-2.5 py-1 text-[12px] font-bold capitalize transition ${
                        activity === tab
                          ? 'bg-navy text-white'
                          : 'text-muted hover:text-navy dark:hover:text-dark-ink'
                      }`}
                    >
                      {tab}
                    </button>
                  ))}
                </span>
              </div>

              {activity === 'history' ? (
                <TicketHistory
                  events={events?.items ?? []}
                  comments={comments?.items ?? []}
                  workflow={workflow}
                  people={people}
                />
              ) : (
                <>
                  <div className="mb-3 flex flex-col gap-2.5">
                    {(comments?.items ?? []).map((entry) => (
                      <article
                        key={entry.id}
                        className={`rounded-panel border p-3 ${
                          entry.isInternal
                            ? 'border-caution/25 bg-caution-soft'
                            : 'border-hairline bg-mist/60 dark:border-dark-line dark:bg-white/5'
                        }`}
                      >
                        <div className="mb-1 flex items-center gap-2">
                          <span className="text-[12.5px] font-bold text-navy dark:text-dark-ink">
                            {entry.author.firstName} {entry.author.lastName}
                          </span>
                          {entry.isInternal ? <Badge tone="caution">Internal</Badge> : null}
                          <span className="ml-auto text-[11.5px] text-muted">
                            {new Date(entry.createdAt).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-[13.5px] leading-relaxed whitespace-pre-wrap text-navy/85 dark:text-dark-ink/85">
                          {entry.body}
                        </p>
                      </article>
                    ))}

                    {(comments?.items ?? []).length === 0 ? (
                      <p className="text-[13.5px] text-muted">No comments yet.</p>
                    ) : null}
                  </div>

                  <textarea
                    value={comment}
                    onChange={(event) => {
                      setComment(event.target.value);
                    }}
                    rows={3}
                    placeholder="Write a comment"
                    className="w-full rounded-field border-[1.5px] border-navy/20 bg-field px-3 py-2 text-[13.5px] text-navy outline-none focus:border-royal/55 focus:bg-white dark:border-dark-line dark:bg-dark-field dark:text-dark-ink"
                  />

                  <div className="mt-2 flex items-center gap-3">
                    <label className="flex items-center gap-2 text-[12.5px] font-semibold text-muted">
                      <input
                        type="checkbox"
                        checked={internal}
                        onChange={(event) => {
                          setInternal(event.target.checked);
                        }}
                      />
                      Internal note
                    </label>

                    <Button
                      size="sm"
                      className="ml-auto"
                      disabled={comment.trim().length === 0}
                      loading={addComment.isPending}
                      onClick={() => {
                        addComment.mutate();
                      }}
                    >
                      Comment
                    </Button>
                  </div>
                </>
              )}
            </section>

            {mayArchive ? (
              <section className="mt-6 border-t border-hairline pt-4 dark:border-dark-line">
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <h3 className="text-[13px] font-bold text-navy dark:text-dark-ink">
                      {ticket.archivedAt === null ? 'Archive this request' : 'Archived'}
                    </h3>
                    <p className="mt-1 text-[12.5px] leading-relaxed text-muted">
                      {ticket.archivedAt === null
                        ? 'Takes it off the board without deleting it. Nothing else changes, and you can restore it at any time from the board’s Archive view.'
                        : 'This request is off the board. Restoring puts it back in the column it left.'}
                    </p>
                  </div>

                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    loading={archive.isPending}
                    onClick={() => {
                      archive.mutate(ticket.archivedAt === null ? 'archive' : 'restore');
                    }}
                  >
                    {ticket.archivedAt === null ? 'Archive' : 'Restore'}
                  </Button>
                </div>
              </section>
            ) : null}

            {mayDelete ? (
              <section className="mt-6 border-t border-hairline pt-4 dark:border-dark-line">
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <h3 className="text-[13px] font-bold text-navy dark:text-dark-ink">
                      Delete this request
                    </h3>
                    <p className="mt-1 text-[12.5px] leading-relaxed text-muted">
                      Its history, comments and attachments go with it. To end a request that ran
                      its course, close it on the board instead — that keeps the record.
                    </p>
                  </div>

                  <Button
                    type="button"
                    variant="danger"
                    size="sm"
                    onClick={() => {
                      setConfirmingDelete(true);
                    }}
                  >
                    Delete
                  </Button>
                </div>
              </section>
            ) : null}
          </div>
        )}
      </aside>

      {confirmingDelete && ticket !== undefined ? (
        <ConfirmDeleteTicket
          number={ticket.number}
          title={ticket.title}
          pending={removeTicket.isPending}
          onCancel={() => {
            setConfirmingDelete(false);
          }}
          onConfirm={() => {
            removeTicket.mutate();
          }}
        />
      ) : null}
    </>
  );
}

interface ConfirmDeleteTicketProps {
  readonly number: number;
  readonly title: string;
  readonly pending: boolean;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}

function ConfirmDeleteTicket({
  number,
  title,
  pending,
  onCancel,
  onConfirm,
}: ConfirmDeleteTicketProps): ReactNode {
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-end bg-navy-deep/45 backdrop-blur-sm sm:place-items-center sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-delete-ticket-title"
    >
      <div className="w-full overflow-hidden rounded-t-card bg-white shadow-menu sm:max-w-[480px] sm:rounded-card dark:bg-dark-panel">
        <div className="h-1 bg-linear-to-r from-gold via-cyan to-royal" />

        <div className="p-5 sm:p-6">
          <h2
            id="confirm-delete-ticket-title"
            className="font-display text-[20px] font-extrabold tracking-[-0.02em] text-navy dark:text-dark-ink"
          >
            Delete SBG#{String(number)}?
          </h2>

          <p className="mt-2 text-[14px] leading-relaxed text-navy/80 dark:text-dark-ink/80">
            <strong>{title}</strong> and everything on it — its history, comments and attachments —
            are deleted for good. This cannot be undone.
          </p>

          <div className="mt-6 flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="button" variant="danger" loading={pending} onClick={onConfirm}>
              Delete request
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
